import React from 'react';
import { Link } from 'react-router-dom';
import { RANK_TIERS, BADGE_DEFS, SCORE_RULES } from '../constants/ranks';
import { Trophy, Zap, Target, ArrowRight, Star, CheckCircle2 } from 'lucide-react';

const RankingGuide: React.FC = () => {
  const maxScore = RANK_TIERS[RANK_TIERS.length - 1].minScore;

  return (
    <div className="min-h-screen bg-tech-dark text-tech-text relative overflow-hidden">
      {/* Ambient glows */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/3 w-[600px] h-[400px] bg-tech-cyan/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[150px]" />
        <div className="absolute top-1/2 left-0 w-[300px] h-[300px] bg-indigo-500/8 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10">
        {/* ── Hero ── */}
        <section className="py-20 px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tech-cyan/10 border border-tech-cyan/20 text-tech-cyan text-sm font-semibold mb-6">
              <Trophy className="w-4 h-4" />
              GNK Continuum Ranking System
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="animated-gradient-text">Your Journey</span>
              <br />
              <span className="text-tech-text">from Seed to Visionary</span>
            </h1>
            <p className="text-xl md:text-2xl text-tech-text-muted max-w-2xl mx-auto leading-relaxed mb-10">
              Every prompt, every task, every connection builds your legend. The more you contribute, the higher you rise — and the more power you unlock.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/task-hatch"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-tech-blue to-tech-cyan text-white font-semibold text-base hover:opacity-90 transition-opacity shadow-[0_0_30px_rgba(34,211,238,0.3)]"
              >
                Start Creating Tasks
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/taskman"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border border-tech-cyan/30 bg-tech-cyan/5 text-tech-cyan font-semibold text-base hover:bg-tech-cyan/10 transition-colors"
              >
                Browse Task Board
              </Link>
            </div>
          </div>
        </section>

        {/* ── How Points Work ── */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                <Zap className="inline-block w-8 h-8 text-tech-cyan mr-2 mb-1" />
                How Points Work
              </h2>
              <p className="text-tech-text-muted text-lg">Every action earns you points. Points determine your rank.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[
                { label: 'Create a task', pts: `+${SCORE_RULES.taskCreated}`, color: 'from-cyan-500/20 to-blue-500/10 border-cyan-400/30', icon: '📝', desc: 'Each new task submitted' },
                { label: 'Post a prompt', pts: `+${SCORE_RULES.promptPosted}`, color: 'from-violet-500/20 to-purple-500/10 border-violet-400/30', icon: '💡', desc: 'Each prompt on the board' },
                { label: 'Complete a task', pts: `+${SCORE_RULES.taskCompleted}`, color: 'from-green-500/20 to-emerald-500/10 border-green-400/30', icon: '✅', desc: 'Marked as completed' },
                { label: 'Sprint Bonus', pts: `+${SCORE_RULES.sprintBonus}`, color: 'from-orange-500/20 to-red-500/10 border-orange-400/30', icon: '🔥', desc: '5+ tasks in one week' },
                { label: 'Prompt Storm', pts: `+${SCORE_RULES.promptStormBonus}`, color: 'from-amber-500/20 to-yellow-500/10 border-amber-400/30', icon: '⚡', desc: '20+ prompts in one week' },
              ].map((item) => (
                <div
                  key={item.label}
                  className={`p-5 rounded-2xl bg-gradient-to-br ${item.color} border glass-panel premium-card-hover`}
                >
                  <div className="text-3xl mb-2">{item.icon}</div>
                  <div className="text-2xl font-bold text-tech-cyan mb-1">{item.pts} pts</div>
                  <div className="font-semibold text-tech-text mb-1">{item.label}</div>
                  <div className="text-sm text-tech-text-muted">{item.desc}</div>
                </div>
              ))}
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-r from-tech-cyan/5 to-blue-500/5 border border-tech-cyan/20 text-center">
              <p className="text-tech-text-muted">
                <span className="text-tech-cyan font-semibold">Pro tip:</span> Sprint weekly to stack bonuses. Creating 5+ tasks per week stacks a +{SCORE_RULES.sprintBonus} bonus every single week — that's over +1,000 bonus points in a year.
              </p>
            </div>
          </div>
        </section>

        {/* ── Rank Tiers ── */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                <Star className="inline-block w-8 h-8 text-amber-400 mr-2 mb-1" />
                The 8 Rank Tiers
              </h2>
              <p className="text-tech-text-muted text-lg">Rise through the ranks as your score grows. Each tier unlocks new prestige and perks.</p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-gradient-to-b from-emerald-400/50 via-cyan-400/50 via-violet-400/50 to-amber-400/50 hidden md:block" />

              <div className="space-y-4">
                {RANK_TIERS.map((tier, idx) => {
                  const relativePosition = tier.minScore === 0 ? 0 : Math.round((tier.minScore / maxScore) * 100);
                  return (
                    <div
                      key={tier.id}
                      className={`relative md:ml-16 p-6 rounded-2xl bg-gradient-to-br ${tier.color} border glass-panel premium-card-hover`}
                      style={{ boxShadow: `0 0 30px ${tier.glowColor}` }}
                    >
                      {/* Timeline dot */}
                      <div
                        className="absolute -left-[52px] top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-lg border-2 border-tech-surface hidden md:flex"
                        style={{ backgroundColor: tier.glowColor, boxShadow: `0 0 15px ${tier.glowColor}` }}
                      >
                        {tier.emoji}
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span className="text-2xl">{tier.emoji}</span>
                            <h3 className="text-xl font-bold text-tech-text">{tier.title}</h3>
                            <span className="px-3 py-0.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold text-tech-text-muted">
                              {tier.minScore === 0 ? 'Starting rank' : `${tier.minScore.toLocaleString()}+ pts`}
                            </span>
                            <span className="text-xs text-tech-text-muted">Tier {idx + 1}</span>
                          </div>
                          <p className="text-tech-text-muted mb-3 italic">"{tier.description}"</p>

                          <div className="flex flex-wrap gap-2">
                            {tier.perks.map((perk) => (
                              <span
                                key={perk}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/8 border border-white/10 text-xs text-tech-text-muted"
                              >
                                <CheckCircle2 className="w-3 h-3 text-tech-cyan shrink-0" />
                                {perk}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="shrink-0 w-28">
                          <div className="text-xs text-tech-text-muted mb-1 text-center">Progress to max</div>
                          <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-tech-cyan to-blue-400 transition-all"
                              style={{ width: `${relativePosition}%` }}
                            />
                          </div>
                          <div className="text-xs text-tech-text-muted mt-1 text-center">{relativePosition}%</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ── Milestone Badges ── */}
        <section className="py-16 px-4 bg-tech-elevated/30">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                <Trophy className="inline-block w-8 h-8 text-amber-400 mr-2 mb-1" />
                Milestone Badges
              </h2>
              <p className="text-tech-text-muted text-lg">
                Special achievements that mark extraordinary milestones. Badges appear on your public profile permanently.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {BADGE_DEFS.map((badge) => (
                <div
                  key={badge.id}
                  className={`p-5 rounded-2xl bg-gradient-to-br ${badge.color} border glass-panel premium-card-hover`}
                >
                  <div className="text-4xl mb-3">{badge.emoji}</div>
                  <h3 className="text-lg font-bold text-tech-text mb-1">{badge.title}</h3>
                  <p className="text-sm text-tech-text-muted leading-relaxed">{badge.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Tips for Climbing ── */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                <Target className="inline-block w-8 h-8 text-tech-cyan mr-2 mb-1" />
                Tips for Climbing Faster
              </h2>
              <p className="text-tech-text-muted text-lg">Smart strategies to accelerate your rank progression.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {[
                {
                  emoji: '🗓️',
                  title: 'Sprint Every Week',
                  desc: 'Create at least 5 tasks per week to trigger the Sprint Bonus (+20 pts). This alone can add over 1,000 bonus points per year on top of your base task points.',
                  color: 'from-orange-500/20 to-red-500/10 border-orange-400/30',
                },
                {
                  emoji: '💬',
                  title: 'Flood the Prompt Board',
                  desc: 'Post 20+ prompts in a single week to trigger the Prompt Storm Bonus (+30 pts). Consistent prompt posting is the fastest way to earn solo-activity points.',
                  color: 'from-violet-500/20 to-purple-500/10 border-violet-400/30',
                },
                {
                  emoji: '✅',
                  title: 'Close the Loop',
                  desc: 'Mark tasks as completed. Completed tasks are worth 50% more than created tasks (+15 vs +10). Track your tasks in the Task Hub and close them out.',
                  color: 'from-green-500/20 to-emerald-500/10 border-green-400/30',
                },
                {
                  emoji: '📅',
                  title: 'Stay Consistent',
                  desc: 'Active 4+ weeks in a row earns the Weekly Streak badge. Consistent contributors climb faster than burst contributors because of weekly bonuses stacking up.',
                  color: 'from-cyan-500/20 to-blue-500/10 border-cyan-400/30',
                },
              ].map((tip) => (
                <div
                  key={tip.title}
                  className={`p-6 rounded-2xl bg-gradient-to-br ${tip.color} border glass-panel premium-card-hover`}
                >
                  <div className="text-3xl mb-3">{tip.emoji}</div>
                  <h3 className="text-lg font-bold text-tech-text mb-2">{tip.title}</h3>
                  <p className="text-sm text-tech-text-muted leading-relaxed">{tip.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-20 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="p-10 rounded-3xl border border-tech-cyan/20 bg-gradient-to-br from-tech-cyan/5 to-blue-500/5 glass-panel"
              style={{ boxShadow: '0 0 60px rgba(34,211,238,0.1)' }}>
              <div className="text-6xl mb-4">🌐</div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 animated-gradient-text">
                Start Building Your Rank Today
              </h2>
              <p className="text-tech-text-muted text-lg mb-8 max-w-xl mx-auto">
                Every task you create, every prompt you post moves you closer to Visionary. The journey begins now.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/task-hatch"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-tech-blue to-tech-cyan text-white font-semibold text-base hover:opacity-90 transition-opacity shadow-[0_0_30px_rgba(34,211,238,0.3)]"
                >
                  Create Your First Task
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/taskman"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-tech-cyan/30 bg-tech-cyan/5 text-tech-cyan font-semibold text-base hover:bg-tech-cyan/10 transition-colors"
                >
                  Browse the Task Board
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RankingGuide;
